import { Global } from "@emotion/react";
import { Css, GridColumn, GridTable, px, RowStyles, SimpleHeaderAndData, useTestIds } from "@homebound/beam";
import { Price } from "src/components";
import { PandaDocRole, ProjectRole } from "src/generated/graphqlTypes";
import { useInvoicePandaDocPdfQuery } from "src/routes/pdfs/InvoicePdf/InvoicePandaDocPdf.generated";
import { queryResult } from "src/utils";
import { StringParam, useQueryParam } from "use-query-params";
import { Initials, Radio } from "../components/PandaDocField";
import { InvoicePdf } from "./InvoicePdf";

export function InvoicePandaDocPdf() {
  const [invoiceId] = useQueryParam("invoiceId", StringParam);
  const query = useInvoicePandaDocPdfQuery({ variables: { invoiceId: invoiceId! } });
  const testIds = useTestIds({}, "invoicePdf");
  const paymentOptions = ["Wire Transfer", "Mailed Check", "Stripe Payment", "Lender"];

  return queryResult(query, ({ invoice }) => {
    const { primaryHomeowner, customer } = invoice.project;
    const projectAccountant = invoice.project.teamMembers.find((tm) => tm.role.code === ProjectRole.Accounting);
    return (
      <>
        <Global
          styles={{ "@page": { size: "letter" }, ul: Css.ml1.my2.add({ listStyleType: "disc" }).$, li: Css.my2.$ }}
        />

        {/* Page 1 */}
        <div css={Css.p2.$}>
          <img src="/wordmark.svg" alt="Homebound" css={Css.h(px(30)).$} />

          <div css={Css.lg.tac.my3.$}>Invoice Payment Request</div>

          <div {...testIds.homeownerName} css={Css.mb2.$}>
            Attention: {customer?.name ?? primaryHomeowner?.fullName ?? "Client"}
          </div>

          <div>
            <p css={Css.mb2.$}>Hello,</p>
            <p css={Css.mb2.$}>
              Please find your invoice attached. You may choose to pay via Stripe, wire transfer, or by mailing a check.
              Alternatively, if you are working with a lender, please indicate this. Payment is due by the due date
              listed on your invoice.
            </p>
            <p>
              Once you've selected your preferred payment method, please initial and click <em>Finish</em>. Regardless
              of the option selected, the next screen will provide the ability to pay via Stripe. If you are paying by
              wire transfer, check, or through a lender, you may disregard this step. Wiring instructions and remittance
              address can be found on the lower portion of the invoice.
            </p>

            <br />

            <div css={Css.p3.$}>
              <Radio role={PandaDocRole.Homeowner} options={paymentOptions} />
            </div>
          </div>

          <div css={Css.mb2.$}>
            <GridTable
              style={{ bordered: true }}
              as="table"
              columns={columns}
              rowStyles={rowStyles}
              rows={[
                { id: invoice.id, data: invoice, kind: "header" },
                { id: invoice.id, data: invoice, kind: "data" },
              ]}
            />
          </div>

          <div css={Css.df.$}>
            <div css={Css.w50.df.fdc.aie.p1.$}>
              <Initials role={PandaDocRole.Homeowner} />
              <div>Homeowner Initials</div>
            </div>
            <div css={Css.w50.df.jcfe.$}>
              <div css={Css.df.fdc.wPx(225).gap2.$}>
                <div css={Css.df.jcsb.$}>
                  <div>Subtotal</div>
                  <strong>
                    <Price id="subtotal" valueInCents={invoice.amountInCents} />
                  </strong>
                </div>
                <div css={Css.f1.df.jcsb.$}>
                  <div>Credit</div>
                  <strong>
                    <Price id="credit" valueInCents={invoice.creditForInvoice?.amountInCents} />
                  </strong>
                </div>
                <div css={Css.f1.fwb.df.jcsb.lg.$}>
                  <div>Total Due</div>
                  <Price
                    id="totalDue"
                    valueInCents={invoice.amountInCents - (invoice.creditForInvoice?.amountInCents ?? 0)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div css={Css.mt1.$}>
            <div>Thank you!</div>
            <div {...testIds.accountantName}>{projectAccountant?.user?.name ?? ""}</div>
            <div {...testIds.accountantEmail}>{projectAccountant?.user?.email ?? ""}</div>
            <div>Project Accountant</div>
          </div>
        </div>

        <div style={{ pageBreakBefore: "always" }}></div>

        {/* Page 2 */}
        <InvoicePdf />
      </>
    );
  });
}

type Row = SimpleHeaderAndData<{ id: string; amountInCents: number }>;

const columns: GridColumn<Row>[] = [
  { header: "Amount Due", data: (invoice) => <Price valueInCents={invoice.amountInCents} />, align: "right" },
  { header: "Qty", data: () => 1, align: "right" },
  { header: "Subtotal", data: (invoice) => <Price valueInCents={invoice.amountInCents} />, align: "right" },
];

export const rowStyles: RowStyles<Row> = { header: { cellCss: Css.bgGray400.$ }, data: { cellCss: Css.bgWhite.$ } };
